import type { Event } from "@sentry/nextjs";

/**
 * Case-insensitive substring fragments. A property key is considered
 * sensitive if its name *contains* any of these — e.g. "passwordConfirmation",
 * "recaptchaToken", "Authorization" all match "password"/"token"/"authorization".
 * Deliberately broad (substring, not exact-name) so a not-yet-invented field
 * name (another *Token, another *Secret) is still caught.
 */
export const SENSITIVE_KEY_FRAGMENTS = [
  "password",
  "passwd",
  "token",
  "secret",
  "authorization",
  "apikey",
  "api_key",
  "cookie",
  "creditcard",
  "cardnumber",
  "socialsecurity",
  "email",
  "phone",
] as const;

/**
 * Short abbreviations matched as a whole *word* of the key (split on
 * camelCase / snake_case / kebab-case) rather than as a substring — as a
 * substring, "ssn" would match "businessName" and "className", and "cell"
 * would match "cancelled".
 */
export const SENSITIVE_KEY_WORDS = [
  "ssn",
  "cvv",
  "cvc",
  "cell",
  "fax",
] as const;

/**
 * Strings longer than this skip the JSON-pair regex (whose lazy value match
 * can go quadratic on a huge string with an unterminated quote) and, if they
 * mention a sensitive key at all, are redacted whole — failing closed.
 */
const MAX_SCRUB_STRING_LENGTH = 50_000;

const REDACTED = "[Filtered]";

/** Bounds recursion so a pathological or circular structure can't hang or overflow the stack. */
const MAX_DEPTH = 20;
const MAX_DEPTH_PLACEHOLDER = "[MaxDepth]";

/**
 * Top-level event keys passed through by reference, unscrubbed. Sentry
 * strips `sdkProcessingMetadata` before the envelope is built (it never
 * leaves the process), and on transactions it holds live SDK internals
 * (`capturedSpanScope` etc.) that must not be rebuilt into plain objects.
 */
const PASSTHROUGH_TOP_LEVEL_KEYS = new Set(["sdkProcessingMetadata"]);

// No lookbehind here: it's a parse-time SyntaxError on Safari < 16.4, which
// copilot-crm's browserslist still supports — and this module loads in
// instrumentation-client, so it would take the whole bundle down.
function keyWords(key: string): string[] {
  return key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .split(/[^a-zA-Z0-9]+/)
    .filter(Boolean)
    .map((word) => word.toLowerCase());
}

function isSensitiveKey(key: string): boolean {
  const lower = key.toLowerCase();
  if (SENSITIVE_KEY_FRAGMENTS.some((fragment) => lower.includes(fragment))) {
    return true;
  }
  const words = keyWords(key);
  return SENSITIVE_KEY_WORDS.some((word) => words.includes(word));
}

/**
 * Matches a JSON `"key": value` pair, including one embedded in an
 * already-stringified JSON string (`\"key\": \"value\"`). Group 1 is the
 * quote-escape prefix (zero or more backslashes), reused via `\1` so the
 * value's quotes are matched at the same escape level as the key's. The
 * value is either a quoted string or a bare scalar (number/bool/null) —
 * nested objects/arrays aren't matched as a value, so their own keys are
 * still scanned individually.
 *
 * Inside a quoted value, a backslash run is consumed whole, together with
 * the character after it (`(?=(\\+))\5` emulates an atomic group). So the
 * value only ends at a quote preceded by *exactly* the key's escape run: an
 * escaped quote inside a doubly-stringified value (`\\\"`) can't end it early
 * and leave the value's tail unredacted.
 */
const JSON_KEY_VALUE_PATTERN =
  /(\\*)"([^"\\]*)\1"(\s*:\s*)(\1"(?:[^"\\]|(?=(\\+))\5[\s\S])*?\1"|[^\s,{}[\]\\"]+)/g;

/**
 * Matches a URL / form-encoded `key=value` parameter at the start of the
 * string or after `?` / `&` — e.g. a reset link's `?token=…` in
 * `request.url`, `request.query_string`, or a fetch/navigation breadcrumb.
 */
const QUERY_PARAM_PATTERN = /(^|[?&])([\w.\-[\]%]+)=([^&#\s"'\\]*)/g;

/**
 * Start of graphql-request's ClientError payload —
 * `${summary}: ${JSON.stringify({ response, request })}` — at any escape
 * level (group 1), so it's also caught inside an already-stringified value.
 * `JSON.stringify` never emits the space after the colon, so plain JSON
 * with a nested `"response"` key doesn't match.
 *
 * Non-global, so `exec` is stateless and safe to share across modules.
 */
export const CLIENT_ERROR_PAYLOAD_PATTERN = /: \{(\\*)"response\1":/;

/**
 * Drops a ClientError's serialized `{ response, request }` payload,
 * keeping the summary before it. Key-based redaction alone only catches
 * *sensitive-named* variables; the payload also carries names, addresses,
 * notes, etc. — and it reaches Sentry via console breadcrumbs
 * (`console.error(error)`) no matter how the error itself was reported.
 */
function stripClientErrorPayload(value: string): string {
  const match = CLIENT_ERROR_PAYLOAD_PATTERN.exec(value);
  return match ? `${value.slice(0, match.index)}: ${REDACTED}` : value;
}

/**
 * Redacts sensitive `"key": value` pairs inside a string. This is what
 * catches graphql-request's ClientError, which bakes the full request —
 * variables included — into its own `.message` via
 * `JSON.stringify({ response, request })`. That string then surfaces in the
 * exception value, `extra.error.message`, console breadcrumbs, etc., where a
 * key-based scrubber alone never sees it (CRM-NEXTJS-B9). Also redacts
 * sensitive URL query / form parameters (see `QUERY_PARAM_PATTERN`).
 */
function scrubString(rawValue: string): string {
  const value = stripClientErrorPayload(rawValue);
  const lower = value.toLowerCase();
  const mightContainSensitiveKey =
    SENSITIVE_KEY_FRAGMENTS.some((fragment) => lower.includes(fragment)) ||
    SENSITIVE_KEY_WORDS.some((word) => lower.includes(word));
  if (!mightContainSensitiveKey) return value;
  if (value.length > MAX_SCRUB_STRING_LENGTH) return REDACTED;

  return scrubJsonPairs(value).replace(
    QUERY_PARAM_PATTERN,
    (match, prefix: string, key: string, paramValue: string) =>
      paramValue && isSensitiveKey(key) ? `${prefix}${key}=${REDACTED}` : match,
  );
}

function scrubJsonPairs(value: string): string {
  return value.replace(
    JSON_KEY_VALUE_PATTERN,
    (
      _match,
      escape: string,
      key: string,
      separator: string,
      pairValue: string,
    ) =>
      `${escape}"${key}${escape}"${separator}${
        isSensitiveKey(key)
          ? `${escape}"${REDACTED}${escape}"`
          : // A non-sensitive string value can itself be stringified JSON
            // (e.g. a request `body`) — scan inside it too.
            scrubString(pairValue)
      }`,
  );
}

/**
 * A GraphQL request object (`{ query, variables }`) — e.g. a ClientError's
 * `request`, which rides along structurally in console breadcrumbs'
 * `data.arguments` and in `extra`. Its `variables` are redacted whole, not
 * just sensitive-named keys: they carry names, addresses, notes, etc. This
 * is the structured counterpart of `stripClientErrorPayload`.
 */
function isGraphQlRequestShape(keys: string[]): boolean {
  return keys.includes("query") && keys.includes("variables");
}

/**
 * `seen` maps each already-visited object to its scrubbed copy. Without it,
 * a shared or circular reference is re-walked on every path that reaches it
 * — exponential in MAX_DEPTH (an object with just two self-references
 * costs ~2^20 visits). Returning the existing copy keeps the walk linear and
 * preserves the original's shape, cycles included, for Sentry's own
 * normalization to handle.
 */
function scrubValue(
  value: unknown,
  depth: number,
  seen: WeakMap<object, unknown>,
): unknown {
  if (typeof value === "string") {
    return scrubString(value);
  }

  if (value === null || typeof value !== "object") {
    return value;
  }

  if (seen.has(value)) {
    return seen.get(value);
  }

  // Fail closed past the depth cap: an unscrubbed subtree could still hold a
  // sensitive field, so replace it rather than pass it through.
  if (depth > MAX_DEPTH) {
    return MAX_DEPTH_PLACEHOLDER;
  }

  // Leave built-ins with no meaningful own properties (and binary data,
  // which holds no keys to match) as-is — rebuilding them via property
  // enumeration would silently turn them into empty objects.
  if (
    value instanceof Date ||
    value instanceof RegExp ||
    value instanceof ArrayBuffer ||
    ArrayBuffer.isView(value)
  ) {
    return value;
  }

  if (Array.isArray(value)) {
    const result: unknown[] = [];
    seen.set(value, result);
    for (const item of value) {
      result.push(scrubValue(item, depth + 1, seen));
    }
    return result;
  }

  if (value instanceof Map) {
    const result = new Map<unknown, unknown>();
    seen.set(value, result);
    for (const [key, entry] of value) {
      result.set(
        key,
        typeof key === "string" && isSensitiveKey(key)
          ? REDACTED
          : scrubValue(entry, depth + 1, seen),
      );
    }
    return result;
  }

  if (value instanceof Set) {
    const result = new Set<unknown>();
    seen.set(value, result);
    for (const entry of value) {
      result.add(scrubValue(entry, depth + 1, seen));
    }
    return result;
  }

  // Any other object — plain or class instance — is rebuilt as a plain
  // object of its own properties (prototype getters/methods are dropped,
  // which Sentry's normalization would do anyway). Use getOwnPropertyNames
  // (not Object.entries/keys) so non-enumerable-but-essential fields like an
  // Error's `message`/`stack` survive the rebuild — a raw Error/ClientError
  // can end up as event data (e.g. `extra.error`) before Sentry's own
  // normalization runs.
  const result: Record<string, unknown> = {};
  seen.set(value, result);
  const ownKeys = Object.getOwnPropertyNames(value);
  const isGraphQlRequest = isGraphQlRequestShape(ownKeys);
  for (const key of ownKeys) {
    try {
      const original = (value as Record<string, unknown>)[key];
      if (depth === 0 && PASSTHROUGH_TOP_LEVEL_KEYS.has(key)) {
        result[key] = original;
        continue;
      }
      result[key] =
        isSensitiveKey(key) || (isGraphQlRequest && key === "variables")
          ? REDACTED
          : scrubValue(original, depth + 1, seen);
    } catch {
      // A getter can throw on access — skip that one field rather than fail the whole event.
    }
  }
  return result;
}

/**
 * Sentry beforeSend / beforeSendTransaction helper: deep-redacts sensitive
 * fields (password, token, secret, etc — see SENSITIVE_KEY_FRAGMENTS /
 * SENSITIVE_KEY_WORDS) anywhere in an outgoing event — exception data,
 * extra, contexts, breadcrumbs, request, spans — regardless of nesting depth
 * or which code path put the data there. Matched values are replaced with
 * "[Filtered]"; the key itself is kept so the event still shows the field
 * existed. String values are also scanned for embedded JSON `"key": value`
 * pairs and URL `key=value` parameters (see `scrubString`).
 *
 * Never throws — but fails *closed*: if scrubbing itself hits an internal
 * error (e.g. a poisoned/revoked Proxy in the event data), this returns
 * `null` rather than the original, unredacted event. A scrubbing bug should
 * cost a dropped event, never a leaked one — `null` is the documented
 * Sentry beforeSend contract for "don't send this event."
 */
export function scrubSensitiveData<TEvent extends Event>(
  event: TEvent,
): TEvent | null {
  try {
    return scrubValue(event, 0, new WeakMap()) as TEvent;
  } catch {
    return null;
  }
}
